import React from "react";
import PropTypes from "prop-types";

import FormikField from "components/forms/formik-field";
import { Form, Formik } from "formik";

import { useFetch } from "hooks";
import { post, put } from "utils/api";
import { getApiErrorMessage } from "utils/misc";
import * as msg from "actions/message";
import { useDispatch } from 'react-redux';

import { errorMessage } from "actions/message";
import moment from "moment";
import { path } from "ramda";

const AddNewTherapyDataEntry = ({
    therapyDataRecord,
    closeForm,
    userName,
    patientId
}) => {


    const defaultValues = {
        SerialNumber: "",
        Source: "",
        ImportDate: "",
        UsageDate: "",
        Usage: "",
        Pressure: "",
        P95: "",
        AHI: "",
        Leak: "",
        CreatedBy: userName
    };


    const validateEmpty = value => {
        if (!value && value !== false) return "Required";
    };
    const validateMaxInput = (value, max) => {
        const emptyError = validateEmpty(value);
        if (emptyError) return emptyError;

        if (Number(value) > max) return `Must be less than ${max}`;
    };

    const dispatch = useDispatch()

    const { fetchData: createTherapyDataEntry, isFetching: isCreatingTherapyDataEntry } = useFetch({
        apiFn: (data) => post(`patients/${patientId}/therapy-data`, data),
        defaultValue: {},
        transformError: path(["response", "body", "status"]),
        onError: error => {
            errorMessage(`Failed to get Forms: ${getApiErrorMessage(error)}`);
        }
    })
    const { fetchData: editTherapyDataEntry, isFetching: isEditingTherapyDataEntry } = useFetch({
        apiFn: (data) => put(`patients/${patientId}/therapy-data/${data.Id}`, data),
        defaultValue: {},
        transformError: path(["response", "body", "status"]),
        onError: error => {
            errorMessage(`Failed to get Forms: ${getApiErrorMessage(error)}`);
        }
    })

    const handleSubmit = async (data) => {

        const isEditingForm = therapyDataRecord?.Id

        const response = isEditingForm
            ? await editTherapyDataEntry(data)
            : await createTherapyDataEntry(data);

        if (response) {
            if (response.success) {
                dispatch(msg.message(response?.message ?? `Entry saved succesfully`))
                closeForm()
            }
        } else {
            dispatch(msg.errorMessage(`There was an error trying to ${isEditingForm ? "edit" : "create"} the record`))
        }

    }

    return (
        <Formik
            style={{ padding: "1em" }}
            initialValues={
                therapyDataRecord != null
                    ? {
                        ...therapyDataRecord,
                        ImportDate: moment(therapyDataRecord?.ImportDate).format("YYYY-MM-DD"),
                        UsageDate: moment(therapyDataRecord?.UsageDate).format("YYYY-MM-DD")
                    }
                    : defaultValues
            }
            enableReinitialize={true}
            onSubmit={(data) => {
                handleSubmit(data);
            }}
        >
            {({ handleChange }) => {
                const isFormDisabled = isCreatingTherapyDataEntry || isEditingTherapyDataEntry;

                return (
                    <Form>
                        <fieldset>
                            <h2>Add Therapy Data</h2>

                            <div className="dd_col-12">
                                <FormikField
                                    name="SerialNumber"
                                    label="Serial Number"
                                    type="text"
                                    validate={validateEmpty}
                                    disabled={isFormDisabled}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="dd_col-12">
                                <FormikField
                                    name="Source"
                                    label="Source"
                                    component="select"
                                    validate={validateEmpty}
                                    onChange={handleChange}
                                    disabled={isFormDisabled}
                                >
                                    <option value="">Select source</option>
                                    <option value="resmed">Resmed</option>
                                    <option value="react">React</option>                                 
                                </FormikField>
                            </div>

                            <div className="dd_col-12">
                                <FormikField
                                    name="ImportDate"
                                    label="Imported Date"
                                    type="date"
                                    validate={validateEmpty}
                                    disabled={isFormDisabled}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="dd_col-12">
                                <FormikField
                                    name="UsageDate"
                                    label="Usage Date"
                                    type="date"
                                    validate={validateEmpty}
                                    disabled={isFormDisabled}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="dd_col-12">
                                <FormikField
                                    name="Usage"
                                    label="Usage"
                                    type="number"
                                    validate={validateEmpty}
                                    disabled={isFormDisabled}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="dd_col-12">
                                <FormikField
                                    name="Pressure"
                                    label="Pressure"
                                    type="number"
                                    step="0.1"
                                    validate={(value) => validateMaxInput(value, 255)}
                                    disabled={isFormDisabled}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="dd_col-12">
                                <FormikField
                                    name="P95"
                                    label="P95"
                                    type="number"
                                    step="0.1"
                                    validate={(value) => validateMaxInput(value, 255)}
                                    disabled={isFormDisabled}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="dd_col-12">
                                <FormikField
                                    name="AHI"
                                    label="AHI"
                                    type="number"
                                    step="0.1"
                                    validate={(value) => validateMaxInput(value, 9.999)}
                                    disabled={isFormDisabled}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="dd_col-12">
                                <FormikField
                                    name="Leak"
                                    label="Leak"
                                    type="number"
                                    step="0.1"
                                    validate={validateEmpty}
                                    disabled={isFormDisabled}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="dd_col-6">
                                <label>Created by</label>
                                <p>{therapyDataRecord?.CreatedBy ?? userName}</p>
                            </div>

                            <div className="dd_col-6" style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}>
                                <button type="submit" disabled={isFormDisabled}>
                                    Submit
                                </button>
                                <button
                                    style={{ backgroundColor: "red" }}
                                    onClick={closeForm}
                                    disabled={isFormDisabled}
                                >
                                    Cancel
                                </button>
                            </div>
                        </fieldset>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default AddNewTherapyDataEntry;

AddNewTherapyDataEntry.propTypes = {
    therapyDataRecord: PropTypes.object,
    closeForm: PropTypes.func,
    userName: PropTypes.string,
    patientId: PropTypes.string,
};